import * as React from 'react';
import React__default, { useMemo, useEffect } from 'react';
import { DefaultElement, DefaultLeaf, withReact, Editable, Slate, useSlateStatic, useSlate } from 'slate-react';
import create from 'zustand';
import { createEditor, Element, Editor, Text } from 'slate';
import { HistoryEditor, withHistory } from 'slate-history';

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var shallow_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, '__esModule', { value: true });

function shallow(objA, objB) {
  if (Object.is(objA, objB)) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  var keysA = Object.keys(objA);

  if (keysA.length !== Object.keys(objB).length) {
    return false;
  }

  for (var i = 0; i < keysA.length; i++) {
    if (!Object.prototype.hasOwnProperty.call(objB, keysA[i]) || !Object.is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }

  return true;
}

exports.default = shallow;
});

var shallow = unwrapExports(shallow_1);

var vanilla = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, '__esModule', { value: true });

function create(createState) {
  var state;
  var listeners = new Set();

  var setState = function setState(partial, replace) {
    var nextState = typeof partial === 'function' ? partial(state) : partial;

    if (nextState !== state) {
      var previousState = state;
      state = replace ? nextState : Object.assign({}, state, nextState);
      listeners.forEach(function (listener) {
        return listener(state, previousState);
      });
    }
  };

  var getState = function getState() {
    return state;
  };

  var subscribeWithSelector = function subscribeWithSelector(listener, selector, equalityFn) {
    if (selector === void 0) {
      selector = getState;
    }

    if (equalityFn === void 0) {
      equalityFn = Object.is;
    }

    var currentSlice = selector(state);

    function listenerToAdd() {
      var nextSlice = selector(state);

      if (!equalityFn(currentSlice, nextSlice)) {
        var previousSlice = currentSlice;
        listener(currentSlice = nextSlice, previousSlice);
      }
    }

    listeners.add(listenerToAdd); // Unsubscribe

    return function () {
      return listeners.delete(listenerToAdd);
    };
  };

  var subscribe = function subscribe(listener, selector, equalityFn) {
    if (selector || equalityFn) {
      return subscribeWithSelector(listener, selector, equalityFn);
    }

    listeners.add(listener); // Unsubscribe

    return function () {
      return listeners.delete(listener);
    };
  };

  var destroy = function destroy() {
    return listeners.clear();
  };

  var api = {
    setState: setState,
    getState: getState,
    subscribe: subscribe,
    destroy: destroy
  };
  state = createState(setState, getState, api);
  return api;
}

exports.default = create;
});

var createVanillaStore = unwrapExports(vanilla);

/**
 * Slate plugins vanilla store.
 * @see zustand vanilla store
 */

const slatePluginsStore = createVanillaStore(() => ({}));
/**
 * Slate plugins store.
 * @see zustand store
 */

const useSlatePluginsStore = create(slatePluginsStore);

/**
 * If id is defined, get the state by id.
 * Else, get the first state.
 */

const getState = (state, id) => {
  if (id) return state[id];
  const keys = Object.keys(state);
  if (!keys.length) return;
  return state[keys[0]];
};
const useStoreState = id => useSlatePluginsStore(state => getState(state, id), shallow);
/**
 * Slate editor with generic type (default is `ReactEditor & HistoryEditor & RandomKeyEditor`).
 */

const useStoreEditor = id => useSlatePluginsStore(state => {
  var _a;

  return (_a = getState(state, id)) === null || _a === void 0 ? void 0 : _a.editor;
});
const useStoreEditorEnabled = id => useSlatePluginsStore(state => {
  var _a;

  return (_a = getState(state, id)) === null || _a === void 0 ? void 0 : _a.enabled;
});
const useStoreEditorValue = id => useSlatePluginsStore(state => {
  var _a;

  return (_a = getState(state, id)) === null || _a === void 0 ? void 0 : _a.value;
});
const useStoreSlatePlugins = id => useSlatePluginsStore(state => {
  var _a;

  return (_a = getState(state, id)) === null || _a === void 0 ? void 0 : _a.plugins;
});
const useStoreSlatePluginKeys = id => useSlatePluginsStore(state => {
  var _a;

  return (_a = getState(state, id)) === null || _a === void 0 ? void 0 : _a.pluginKeys;
});
const useStoreEditorOptions = id => {
  var _a;

  return (_a = useStoreEditor(id)) === null || _a === void 0 ? void 0 : _a.options;
};

/**
 * @see {@link Decorate}.
 * Optimization: return undefined if empty list so Editable uses a memo.
 */
const pipeDecorate = (editor, plugins = []) => {
  const decorates = plugins.flatMap(plugin => {
    var _a, _b;

    return (_b = (_a = plugin.decorate) === null || _a === void 0 ? void 0 : _a.call(plugin, editor)) !== null && _b !== void 0 ? _b : [];
  });
  if (!decorates.length) return;
  return entry => {
    let ranges = [];

    const addRanges = newRanges => {
      if (newRanges === null || newRanges === void 0 ? void 0 : newRanges.length) ranges = [...ranges, ...newRanges];
    };

    decorates.forEach(decorate => {
      addRanges(decorate(entry));
    });
    return ranges;
  };
};

/**
 * @see {@link OnDOMBeforeInput}
 */
const pipeOnDOMBeforeInput = (editor, plugins = []) => {
  const onDOMBeforeInputs = plugins.flatMap(plugin => {
    var _a, _b;

    return (_b = (_a = plugin.onDOMBeforeInput) === null || _a === void 0 ? void 0 : _a.call(plugin, editor)) !== null && _b !== void 0 ? _b : [];
  });
  return event => {
    onDOMBeforeInputs.some(onDOMBeforeInput => onDOMBeforeInput(event) === false);
  };
};

/**
 * @see {@link OnKeyDown}
 */
const pipeOnKeyDown = (editor, plugins = []) => {
  const onKeyDowns = plugins.flatMap(plugin => {
    var _a, _b;

    return (_b = (_a = plugin.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(plugin, editor)) !== null && _b !== void 0 ? _b : [];
  });
  return event => {
    onKeyDowns.some(onKeyDown => onKeyDown(event) === false);
  };
};

/**
 * @see {@link RenderElement}
 */

const pipeRenderElement = (editor, plugins = []) => {
  const renderElements = plugins.flatMap(plugin => {
    var _a, _b;

    return (_b = (_a = plugin.renderElement) === null || _a === void 0 ? void 0 : _a.call(plugin, editor)) !== null && _b !== void 0 ? _b : [];
  });
  return elementProps => {
    let element;
    renderElements.some(renderElement => {
      element = renderElement(elementProps);
      return !!element;
    });
    if (element) return element;
    return /*#__PURE__*/React.createElement(DefaultElement, Object.assign({}, elementProps));
  };
};

/**
 * @see {@link RenderLeaf}
 */

const pipeRenderLeaf = (editor, plugins = []) => {
  const renderLeafs = plugins.flatMap(plugin => {
    var _a, _b;

    return (_b = (_a = plugin.renderLeaf) === null || _a === void 0 ? void 0 : _a.call(plugin, editor)) !== null && _b !== void 0 ? _b : [];
  });
  return props => {
    const leafProps = Object.assign({}, props); // workaround for children readonly error.

    renderLeafs.forEach(renderLeaf => {
      const newChildren = renderLeaf(leafProps);

      if (newChildren !== undefined) {
        leafProps.children = newChildren;
      }
    });
    return /*#__PURE__*/React.createElement(DefaultLeaf, Object.assign({}, leafProps));
  };
};

const useEditableProps = ({
  id,
  editableProps
}) => {
  const editor = useStoreEditor(id);
  const plugins = useStoreSlatePlugins(id);
  const props = useMemo(() => {
    if (!editor) return {};
    return {
      renderElement: pipeRenderElement(editor, plugins),
      renderLeaf: pipeRenderLeaf(editor, plugins),
      onKeyDown: pipeOnKeyDown(editor, plugins),
      decorate: pipeDecorate(editor, plugins),
      onDOMBeforeInput: pipeOnDOMBeforeInput(editor, plugins)
    };
  }, [editor, plugins]);
  return useMemo(() => Object.assign(Object.assign({}, props), editableProps), [editableProps, props]);
};

const getSlatePluginWithOverrides = withOverrides => options => ({
  withOverrides: withOverrides(options)
});

/**
 * {@link withHistory} that can be called multiple times without losing its history.
 */

const withHistoryPersist = editor => {
  if (HistoryEditor.isHistoryEditor(editor)) {
    const {
      history
    } = editor;
    const editorWithHistory = withHistory(editor);
    editorWithHistory.history = history;
    return editorWithHistory;
  }

  return withHistory(editor);
};
/**
 * @see {@link withHistoryPersist}
 */

const createHistoryPlugin = getSlatePluginWithOverrides(() => withHistory);

/**
 * @see {@link withReact}
 */

const createReactPlugin = getSlatePluginWithOverrides(() => withReact);

function pipe(x, ...fns) {
  return fns.reduce((y, fn) => fn(y), x);
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/**
 * Casts `value` as an array if it's not one.
 *
 * @static
 * @memberOf _
 * @since 4.4.0
 * @category Lang
 * @param {*} value The value to inspect.
 * @returns {Array} Returns the cast array.
 * @example
 *
 * _.castArray(1);
 * // => [1]
 *
 * _.castArray({ 'a': 1 });
 * // => [{ 'a': 1 }]
 *
 * _.castArray('abc');
 * // => ['abc']
 *
 * _.castArray(null);
 * // => [null]
 *
 * _.castArray(undefined);
 * // => [undefined]
 *
 * _.castArray();
 * // => []
 *
 * var array = [1, 2, 3];
 * console.log(_.castArray(array) === array);
 * // => true
 */
function castArray() {
  if (!arguments.length) {
    return [];
  }
  var value = arguments[0];
  return isArray_1(value) ? value : [value];
}

var castArray_1 = castArray;

/**
 * Merge and register all the inline types and void types from the plugins and options,
 * using `editor.isInline` and `editor.isVoid`
 */

const withInlineVoid = ({
  plugins = [],
  inlineTypes = [],
  voidTypes = []
}) => editor => {
  const {
    isInline
  } = editor;
  const {
    isVoid
  } = editor;
  let allInlineTypes = [...inlineTypes];
  let allVoidTypes = [...voidTypes];
  plugins.forEach(plugin => {
    if (plugin.inlineTypes) {
      allInlineTypes = allInlineTypes.concat(castArray_1(plugin.inlineTypes(editor)));
    }

    if (plugin.voidTypes) {
      allVoidTypes = allVoidTypes.concat(castArray_1(plugin.voidTypes(editor)));
    }
  });

  editor.isInline = element => {
    return allInlineTypes.includes(element.type) ? true : isInline(element);
  };

  editor.isVoid = element => allVoidTypes.includes(element.type) ? true : isVoid(element);

  return editor;
};
/**
 * @see {@link withInlineVoid}
 */

const createInlineVoidPlugin = getSlatePluginWithOverrides(withInlineVoid);

/**
 * Flat map an array of object by key.
 */
const flatMapByKey = (arr, key) => arr.flatMap(item => {
  var _a;

  return (_a = item[key]) !== null && _a !== void 0 ? _a : [];
});

/**
 * Apply `withInlineVoid` and all slate plugins `withOverrides`.
 * Overrides:
 * - `id`: id of the editor.
 * - `key`: random key for the <Slate> component so each time the editor is created, the component resets.
 * - `options`: {@link SlatePluginsOptions}
 */

const withSlatePlugins = ({
  id = 'main',
  plugins = [createReactPlugin(), createHistoryPlugin()],
  options = {},
  components = {}
} = {}) => e => {
  let editor = e;
  editor.id = id;

  if (components) {
    // Merge components into options
    Object.keys(components).forEach(key => {
      options[key] = Object.assign({
        component: components[key]
      }, options[key]);
    });
  } // Default option type is the plugin key


  Object.keys(options).forEach(key => {
    if (options[key].type === undefined) options[key].type = key;
  });
  editor.options = options;

  if (!editor.key) {
    editor.key = Math.random();
  } // Plugins inline and void types


  editor = pipe(editor, withInlineVoid({
    plugins
  })); // Plugins withOverrides

  const withOverrides = flatMapByKey(plugins, 'withOverrides');
  editor = pipe(editor, ...withOverrides);
  return editor;
};

const set$1 = slatePluginsStore.setState;
const getStateById = (state, id) => {
  var _a;

  return (_a = state[id]) !== null && _a !== void 0 ? _a : {};
};
const mergeState = (stateToMerge, stateId) => stateId && set$1(state => ({
  [stateId]: Object.assign(Object.assign({}, getStateById(state, stateId)), stateToMerge)
}));
const getSetStateByKey = (key, stateId) => (value, id = stateId) => id && set$1(state => {
  if (!state[id]) return;
  return {
    [id]: Object.assign(Object.assign({}, getStateById(state, id)), {
      [key]: value
    })
  };
});

const {
  getState: get,
  setState: set
} = slatePluginsStore;
const useSlatePluginsActions = storeId => {
  const storeKeys = useSlatePluginsStore(s => Object.keys(s), shallow);
  const stateId = storeId !== null && storeId !== void 0 ? storeId : storeKeys[0];
  return useMemo(() => {
    const setEditor = getSetStateByKey('editor', stateId);
    const setValue = getSetStateByKey('value', stateId);
    return {
      setEditor,
      setValue,
      clearState: (id = stateId) => id && set(state => {
        delete state[id];
      }),
      setInitialState: ({
        enabled = true,
        value = [{
          children: [{
            text: ''
          }]
        }],
        plugins = [],
        pluginKeys = []
      } = {}, id = stateId) => id && set(state => {
        if (state[id]) return;
        state[id] = {
          enabled,
          plugins,
          pluginKeys,
          value
        };
      }),
      resetEditor: (id = stateId) => {
        const state = !!id && get()[id];
        if (!state) return;
        const {
          editor,
          plugins
        } = state;
        setEditor(pipe(createEditor(), withSlatePlugins({
          id,
          plugins,
          options: editor === null || editor === void 0 ? void 0 : editor.options
        })), id);
      },
      setEnabled: getSetStateByKey('enabled', stateId),
      setPlugins: getSetStateByKey('plugins', stateId),
      setPluginKeys: getSetStateByKey('pluginKeys', stateId)
    };
  }, [stateId]);
};

/**
 * Effects to update the slate plugins store from the options.
 * Dynamically updating the options will update the store state.
 */

const useSlatePluginsEffects = ({
  id = 'main',
  value,
  editor,
  enabled = true,
  components,
  options,
  initialValue,
  plugins
}) => {
  const {
    setInitialState,
    setValue,
    setEditor,
    setPlugins,
    setPluginKeys,
    setEnabled,
    clearState
  } = useSlatePluginsActions(id);
  const storeEditor = useStoreEditor(id);
  const storeEnabled = useStoreEditorEnabled(id);
  const storePlugins = useStoreSlatePlugins(id); // Clear the state on unmount.

  useEffect(() => () => {
    clearState();
  }, [clearState, id]);
  useEffect(() => {
    setInitialState({
      enabled: true,
      plugins: [],
      pluginKeys: [],
      value: []
    });
  }, [setInitialState]); // Slate.value

  useEffect(() => {
    initialValue && setValue(initialValue); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]); // Slate.value

  useEffect(() => {
    value && setValue(value);
    !initialValue && !value && setValue([{
      children: [{
        text: ''
      }]
    }]);
  }, [initialValue, setValue, value]);
  useEffect(() => {
    setEnabled(enabled);
  }, [enabled, setEnabled]); // Slate plugins

  useEffect(() => {
    setPlugins(plugins !== null && plugins !== void 0 ? plugins : [createReactPlugin(), createHistoryPlugin()]);
  }, [plugins, setPlugins]);
  useEffect(() => {
    plugins && setPluginKeys(flatMapByKey(plugins, 'pluginKeys'));
  }, [plugins, setPluginKeys]);
  useEffect(() => {
    if (storeEditor && !storeEnabled) {
      setEditor(undefined);
    }
  }, [storeEnabled, setEditor, storeEditor]); // Slate.editor

  useEffect(() => {
    if (!storeEditor && storeEnabled) {
      setEditor(pipe(editor !== null && editor !== void 0 ? editor : createEditor(), withSlatePlugins({
        id,
        plugins: storePlugins,
        options,
        components
      })));
    }
  }, [storeEditor, components, editor, id, options, plugins, setEditor, storeEnabled, storePlugins]);
};

const pipeOnChange = (editor, plugins = []) => {
  const onChanges = plugins.flatMap(plugin => {
    var _a, _b;

    return (_b = (_a = plugin.onChange) === null || _a === void 0 ? void 0 : _a.call(plugin, editor)) !== null && _b !== void 0 ? _b : [];
  });
  return nodes => {
    onChanges.some(onChange => onChange(nodes) === false);
  };
};

/**
 * Get Slate props stored in a global store.
 */

const useSlateProps = ({
  id,
  onChange: _onChange
} = {}) => {
  const {
    setValue
  } = useSlatePluginsActions(id);
  const editor = useStoreEditor(id);
  const value = useStoreEditorValue(id);
  const plugins = useStoreSlatePlugins(id);
  return useMemo(() => ({
    key: editor === null || editor === void 0 ? void 0 : editor.key,
    editor,
    onChange: newValue => {
      setValue(newValue);
      editor && pipeOnChange(editor, plugins)(newValue);
      _onChange === null || _onChange === void 0 ? void 0 : _onChange(newValue);
    },
    value
  }), [_onChange, editor, plugins, setValue, value]);
};

/**
 * Run `useSlatePluginsEffects` and props getter for `Slate` and `Editable` components.
 * Use `useSlatePluginsStore` to select store state.
 */

const useSlatePlugins = ({
  id,
  components,
  editor,
  initialValue,
  value,
  options,
  plugins,
  onChange,
  editableProps
}) => {
  useSlatePluginsEffects({
    id,
    components,
    plugins,
    initialValue,
    editor,
    value,
    options
  });
  return {
    slateProps: useSlateProps({
      id,
      onChange
    }),
    editableProps: useEditableProps({
      id,
      editableProps
    })
  };
};

/**
 * {@link Editable} with plugins support.
 */

const EditablePlugins = props => {
  const {
    editableProps
  } = useSlatePlugins(props);
  return /*#__PURE__*/React.createElement(Editable, Object.assign({}, editableProps));
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const SlatePlugins = _a => {
  var {
    children
  } = _a,
      options = __rest(_a, ["children"]);

  const {
    slateProps,
    editableProps
  } = useSlatePlugins(options);
  if (!slateProps.editor) return null;
  return /*#__PURE__*/React__default.createElement(Slate, Object.assign({}, slateProps), children, /*#__PURE__*/React__default.createElement(Editable, Object.assign({}, editableProps)));
};

/**
 * Typed {@link useSlateStatic} & SPEditor.
 */

const useTSlateStatic = () => useSlateStatic();

/**
 * Editor id stored in `editor`.
 */

const useEditorId = () => useTSlateStatic().id;

/**
 * Get editor.options.
 */
const getEditorOptions = editor => {
  var _a, _b;

  return (_b = (_a = editor) === null || _a === void 0 ? void 0 : _a.options) !== null && _b !== void 0 ? _b : {};
};

/**
 * @see {@link getEditorOptions}
 */

const useEditorOptions = () => {
  return getEditorOptions(useTSlateStatic());
};

/**
 * Get SP options by plugin key.
 */

const getSlatePluginOptions = (editor, pluginKey) => {
  var _a;

  return (_a = getEditorOptions(editor)[pluginKey]) !== null && _a !== void 0 ? _a : {};
};

/**
 * @see {@link getSlatePluginOptions}
 */

const useSlatePluginOptions = pluginKey => {
  return getSlatePluginOptions(useTSlateStatic(), pluginKey);
};

/**
 * Get SP type option by plugin key.
 */

const getSlatePluginType = (editor, pluginKey) => {
  var _a;

  return (_a = getSlatePluginOptions(editor, pluginKey).type) !== null && _a !== void 0 ? _a : pluginKey;
};

/**
 * @see {@link getSlatePluginType}
 */

const useSlatePluginType = pluginKey => {
  return getSlatePluginType(useTSlateStatic(), pluginKey);
};

/**
 * Typed {@link useSlate} & SPEditor.
 */

const useTSlate = () => useSlate();

const isElement = Element.isElement;

const isAncestor = node => Editor.isEditor(node) || isElement(node);

const isText = Text.isText;

const isDescendant = node => isElement(node) || isText(node);

/**
 * Get slate class name: slate-<type>
 */
const getSlateClass = type => `slate-${type}`;

/**
 * Get a `Editable.renderElement` handler for `options.type`.
 * If the type is equals to the slate element type, render `options.component`.
 * Else, return `undefined` so the pipeline can check the next plugin.
 */

const getEditableRenderElement = options => ({
  attributes,
  element,
  children
}) => {
  var _a, _b;

  const _options = castArray_1(options);

  for (const option of _options) {
    const {
      type,
      component: Element = DefaultElement,
      getNodeProps
    } = option;

    if (element.type === type) {
      const nodeProps = (_b = (_a = getNodeProps === null || getNodeProps === void 0 ? void 0 : getNodeProps({
        attributes,
        element,
        children
      })) !== null && _a !== void 0 ? _a : element.attributes) !== null && _b !== void 0 ? _b : {};
      return /*#__PURE__*/React.createElement(Element, {
        className: getSlateClass(type),
        attributes: attributes,
        element: element,
        nodeProps: nodeProps
      }, children);
    }
  }
};

/**
 * Get a `Editable.renderLeaf` handler for `options.type`.
 * If the type is equals to the slate leaf type and if the text is not empty, render `options.component`.
 * Else, return `children`.
 */

const getEditableRenderLeaf = ({
  type,
  component: Leaf = DefaultLeaf,
  getNodeProps
}) => ({
  children,
  leaf,
  text,
  attributes
}) => {
  var _a, _b;

  const nodeProps = (_b = (_a = getNodeProps === null || getNodeProps === void 0 ? void 0 : getNodeProps({
    leaf,
    text,
    attributes,
    children
  })) !== null && _a !== void 0 ? _a : leaf.attributes) !== null && _b !== void 0 ? _b : {};

  if (leaf[type] && !!leaf.text) {
    return /*#__PURE__*/React.createElement(Leaf, {
      className: getSlateClass(type),
      attributes: attributes,
      leaf: leaf,
      text: text,
      nodeProps: nodeProps
    }, children);
  }

  return children;
};

/**
 * Get inline types from the plugins
 */
const getInlineTypes = (editor, plugins) => {
  return plugins.flatMap(p => {
    var _a, _b;

    return (_b = (_a = p.inlineTypes) === null || _a === void 0 ? void 0 : _a.call(p, editor)) !== null && _b !== void 0 ? _b : [];
  });
};

/**
 * Map plugins keys to SP options
 */

const mapSlatePluginKeysToOptions = (editor, pluginKey) => {
  const pluginKeys = castArray_1(pluginKey);

  return pluginKeys.map(key => getSlatePluginOptions(editor, key));
};

/**
 * Get `renderElement` by plugin key.
 * @see {@link getEditableRenderElement}
 */

const getRenderElement = pluginKeys => editor => getEditableRenderElement(mapSlatePluginKeysToOptions(editor, pluginKeys));

/**
 * Get `renderLeaf` by plugin key.
 * @see {@link getEditableRenderLeaf}
 */

const getRenderLeaf = pluginKey => editor => getEditableRenderLeaf(getSlatePluginOptions(editor, pluginKey));

const getSlatePluginTypes = pluginKey => editor => {
  const pluginKeys = castArray_1(pluginKey);

  return pluginKeys.map(key => getSlatePluginType(editor, key));
};

/**
 * Get void types from the plugins
 */
const getVoidTypes = (editor, plugins) => {
  return plugins.flatMap(p => {
    var _a, _b;

    return (_b = (_a = p.voidTypes) === null || _a === void 0 ? void 0 : _a.call(p, editor)) !== null && _b !== void 0 ? _b : [];
  });
};

export { EditablePlugins, SlatePlugins, createHistoryPlugin, createInlineVoidPlugin, createReactPlugin, flatMapByKey, getEditableRenderElement, getEditableRenderLeaf, getEditorOptions, getInlineTypes, getRenderElement, getRenderLeaf, getSetStateByKey, getSlateClass, getSlatePluginOptions, getSlatePluginType, getSlatePluginTypes, getSlatePluginWithOverrides, getState, getStateById, getVoidTypes, isAncestor, isDescendant, isElement, isText, mapSlatePluginKeysToOptions, mergeState, pipe, pipeDecorate, pipeOnChange, pipeOnDOMBeforeInput, pipeOnKeyDown, pipeRenderElement, pipeRenderLeaf, slatePluginsStore, useEditableProps, useEditorId, useEditorOptions, useSlatePluginOptions, useSlatePluginType, useSlatePlugins, useSlatePluginsActions, useSlatePluginsEffects, useSlatePluginsStore, useSlateProps, useStoreEditor, useStoreEditorEnabled, useStoreEditorOptions, useStoreEditorValue, useStoreSlatePluginKeys, useStoreSlatePlugins, useStoreState, useTSlate, useTSlateStatic, withHistoryPersist, withInlineVoid, withSlatePlugins };
//# sourceMappingURL=index.es.js.map
